import { useState, useMemo, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "../../lib/hooks/use-auth";
import { useChatScope } from "../hooks/use-chat-scope";
import { useListTree } from "../../lib/hooks/use-kuvio";
import { useWorkspaceActivities } from "../hooks/use-activities";
import { EmptyState } from "../components/primitives";
import { ActivityHeader } from "../components/activity/ActivityHeader";
import { ActivityRow } from "../components/cockpit/ActivityPanel";
import type { FilterState } from "../components/activity/FilterChips";
import type { Activity, ActivityChannel } from "../../lib/types";

/**
 * Sprint 26.2 — the workspace Activity stream over `list_activities` (the
 * interaction-history primitive), filterable by channel / actor / since.
 *
 * Earlier the route rendered the agent-runs/tool-surface feed; the spec moves it
 * onto the Activity primitive so the same channel enum (email/call/meeting/…)
 * the agent Activity tab uses is filterable here too.
 */

function sinceToIso(since: string): string | undefined {
  if (!since || since === "") return undefined;
  const now = new Date();
  if (since === "today") {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  }
  if (since === "week") {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()).toISOString();
  }
  return undefined;
}

interface DayBucket {
  label: string;
  items: Activity[];
}

function bucketByDay(items: Activity[]): DayBucket[] {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();
  const order: string[] = [];
  const map = new Map<string, DayBucket>();
  for (const item of items) {
    const d = new Date(item.occurred_at);
    const key = d.toDateString();
    const label = key === today ? "Today" : key === yesterday ? "Yesterday"
      : d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { label, items: [] };
      map.set(key, bucket);
      order.push(key);
    }
    bucket.items.push(item);
  }
  return order.map((k) => map.get(k)!);
}

export function ActivityPage() {
  const { currentWorkspaceId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(50);
  const tree = useListTree(currentWorkspaceId ?? undefined);
  useChatScope({ kind: "workspace", label: "Activity" }, null, []);

  const filters: FilterState = {
    agentId: searchParams.get("agentId") ?? "",
    channel: searchParams.get("channel") ?? "",
    since: searchParams.get("since") ?? "today",
  };

  function updateFilters(next: FilterState) {
    const params: Record<string, string> = {};
    if (next.agentId) params.agentId = next.agentId;
    if (next.channel) params.channel = next.channel;
    if (next.since && next.since !== "today") params.since = next.since;
    setSearchParams(params);
    setLimit(50);
  }

  const activity = useWorkspaceActivities({
    channel: (filters.channel || undefined) as ActivityChannel | undefined,
    actorId: filters.agentId || undefined,
    since: sinceToIso(filters.since),
    limit,
  });

  const allNodes = tree.data ?? [];
  const agents = allNodes.filter((n) => n.type === "agent");

  const items = useMemo(() => activity.data ?? [], [activity.data]);
  const buckets = useMemo(() => bucketByDay(items), [items]);
  const todayStr = new Date().toDateString();
  const todayCount = items.filter((i) => new Date(i.occurred_at).toDateString() === todayStr).length;

  // list_activities pages by limit; "more" likely exists when we filled the page.
  const maybeMore = items.length >= limit;

  if (activity.isLoading && items.length === 0) {
    return (
      <div style={{ fontFamily: "var(--font-sans)", maxWidth: 680 }}>
        <div style={{ height: 24, width: 120, background: "var(--color-background-secondary)", borderRadius: 4, marginBottom: 24 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ height: 56, background: "var(--color-background-secondary)", borderRadius: 6, marginBottom: 8 }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-sans)", maxWidth: 680 }}>
      <ActivityHeader
        eventCount={todayCount}
        filters={filters}
        onFiltersChange={updateFilters}
        agents={agents}
      />

      {items.length === 0 ? (
        <EmptyState>No activity matches these filters. Try widening the channel or time range.</EmptyState>
      ) : (
        <>
          {buckets.map((bucket) => (
            <div key={bucket.label} style={{ marginBottom: 16 }}>
              <div style={bucketLabel}>{bucket.label}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {bucket.items.map((a) => (
                  <ActivityRow key={a.id} activity={a} deletable />
                ))}
              </div>
            </div>
          ))}

          {maybeMore && (
            <div style={loadMoreRow}>
              <span
                onClick={() => setLimit((l) => l + 50)}
                style={{ fontSize: 12, color: "var(--color-text-info)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                Load earlier activity
                <i className="ti ti-chevron-down" style={{ fontSize: 12 }} aria-hidden="true" />
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const bucketLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  marginBottom: 6,
};

const loadMoreRow: CSSProperties = {
  textAlign: "center",
  padding: "16px 0",
  borderTop: "var(--kuvio-border-default)",
  marginTop: 8,
};
